import axios from "./axiosInstance";
import config from "./config";
import { captureException } from "../utils/sentry";

const BASE_URL = config.BASE_URL;

const getHeaders = (token?: string) => ({
  "Content-Type": "application/json",
  ...(token ? { authorization: `Bearer ${token}` } : {}),
});

// ============================================
// AI CONFIG
// ============================================
export const getAiConfig = async (token: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/ai/config`, {
      headers: getHeaders(token),
    });
    return response.data;
  } catch (err) {
    captureException(err);
    return null;
  }
};

export const updateAiConfig = async (token: string, configData: Record<string, unknown>) => {
  try {
    const response = await axios.put(`${BASE_URL}/ai/config`, configData, {
      headers: getHeaders(token),
    });
    return response.data;
  } catch (err) {
    captureException(err);
    return null;
  }
};

// Support extensions — staff escalation phone directory (for staff_member
// transfer mode on the AI receptionist config page).
export const getSupportStaff = async (token: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/ai/support-staff`, {
      headers: getHeaders(token),
    });
    return response.data;
  } catch (err) {
    captureException(err);
    return null;
  }
};

export const updateSupportStaffPhone = async (
  token: string,
  userId: string,
  supportPhone: string
) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/ai/support-staff/${userId}`,
      { support_phone: supportPhone },
      { headers: getHeaders(token) }
    );
    return response.data;
  } catch (err) {
    captureException(err);
    return null;
  }
};

// ============================================
// KNOWLEDGE BASE
// ============================================
export const getKnowledgeEntries = async (token: string, params?: Record<string, string>) => {
  try {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : "";
    const response = await axios.get(`${BASE_URL}/ai/knowledge${queryString}`, {
      headers: getHeaders(token),
    });
    return response.data;
  } catch (err) {
    captureException(err);
    return null;
  }
};

export const createKnowledgeEntry = async (token: string, entry: Record<string, unknown>) => {
  try {
    const response = await axios.post(`${BASE_URL}/ai/knowledge`, entry, {
      headers: getHeaders(token),
    });
    return response.data;
  } catch (err) {
    captureException(err);
    return null;
  }
};

export const updateKnowledgeEntry = async (token: string, id: string, entry: Record<string, unknown>) => {
  try {
    const response = await axios.put(`${BASE_URL}/ai/knowledge/${id}`, entry, {
      headers: getHeaders(token),
    });
    return response.data;
  } catch (err) {
    captureException(err);
    return null;
  }
};

export const deleteKnowledgeEntry = async (token: string, id: string) => {
  try {
    const response = await axios.delete(`${BASE_URL}/ai/knowledge/${id}`, {
      headers: getHeaders(token),
    });
    return response.data;
  } catch (err) {
    captureException(err);
    return null;
  }
};

export const autoGenerateKnowledge = async (token: string) => {
  try {
    const response = await axios.post(`${BASE_URL}/ai/knowledge/auto-generate`, {}, {
      headers: getHeaders(token),
    });
    return response.data;
  } catch (err) {
    captureException(err);
    return null;
  }
};

// ============================================
// CONVERSATIONS
// ============================================
export const getAiConversations = async (token: string, params?: Record<string, string>) => {
  try {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : "";
    const response = await axios.get(`${BASE_URL}/ai/conversations${queryString}`, {
      headers: getHeaders(token),
    });
    return response.data;
  } catch (err) {
    captureException(err);
    return null;
  }
};

export const getAiConversationById = async (token: string, id: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/ai/conversations/${id}`, {
      headers: getHeaders(token),
    });
    return response.data;
  } catch (err) {
    captureException(err);
    return null;
  }
};

// ============================================
// USAGE
// ============================================
export const getAiUsageStats = async (token: string, params?: Record<string, string>) => {
  try {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : "";
    const response = await axios.get(`${BASE_URL}/ai/usage${queryString}`, {
      headers: getHeaders(token),
    });
    return response.data;
  } catch (err) {
    captureException(err);
    return null;
  }
};

export interface AiAnalyticsData {
  window: { days: number; since: string };
  totals: {
    total_conversations: number;
    deflected: number;
    escalated: number;
    deflection_rate: number;
    escalation_rate: number;
    conversions_with_lead: number;
    conversions_with_membership: number;
    conversion_rate: number;
  };
  by_channel: Record<string, number>;
  voice: { calls: number; total_minutes: number; avg_duration_seconds: number };
  top_intents: { intent: string; count: number }[];
}

export const getAiAnalytics = async (
  token: string,
  days = 30
): Promise<{ success: boolean; data?: AiAnalyticsData; error?: string } | null> => {
  try {
    const response = await axios.get(`${BASE_URL}/ai/analytics?days=${days}`, {
      headers: getHeaders(token),
    });
    return response.data;
  } catch (err) {
    captureException(err);
    return null;
  }
};

// ============================================
// PUBLIC CHAT (no auth required)
// ============================================
export const sendChatMessage = async (message: string, sessionId?: string) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/ai/chat`,
      { message, session_id: sessionId },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (err) {
    captureException(err);
    return null;
  }
};

// ============================================
// MEMBER AI CHAT (with category + user context)
// ============================================
export const sendAiChatMessage = async (
  message: string,
  sessionId: string,
  category?: string,
  userContext?: {
    name: string;
    email: string;
    membership_id: string;
    // Page context captured at the call site. Lets the LLM
    // disambiguate intent (e.g. "I want to cancel" on the events
    // page vs the membership page).
    page_path?: string;
    page_url?: string;
    page_title?: string;
  }
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/ai/chat`,
      { message, session_id: sessionId, category, user_context: userContext },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (err) {
    captureException(err);
    return null;
  }
};

// ============================================
// AI MARKETING — broadcast draft assist
// ============================================
export type AIDraftChannel = "email" | "sms" | "in_app_post";
export type AIDraftTone = "friendly" | "formal" | "concise" | "energetic";

export interface AIDraftBroadcastResponse {
  subject: string;
  email_body: string;
  sms_body: string;
  post_title: string;
  post_body: string;
  rationale: string;
  usage?: { input_tokens: number; output_tokens: number };
  _parse_failed?: boolean;
}

export const draftBroadcast = async (
  token: string,
  payload: { goal: string; channels?: AIDraftChannel[]; tone?: AIDraftTone }
): Promise<{ success: boolean; data?: AIDraftBroadcastResponse; error?: string } | null> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/ai/marketing/draft-broadcast`,
      payload,
      { headers: getHeaders(token) }
    );
    return response.data;
  } catch (err: unknown) {
    captureException(err);
    const e = err as { response?: { data?: { error?: string } } };
    return { success: false, error: e?.response?.data?.error || "Failed to draft broadcast" };
  }
};


// ============================================
// AI MARKETING — contact extraction
// ============================================
export interface ExtractedContact {
  email: string;
  name: string;
  organization: string;
  role: string;
}

export interface AIExtractContactsResponse {
  contacts: ExtractedContact[];
  extracted_count: number;
  raw_count: number;
  usage?: { input_tokens: number; output_tokens: number };
}

export const extractContacts = async (
  token: string,
  text: string
): Promise<{ success: boolean; data?: AIExtractContactsResponse; error?: string } | null> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/ai/marketing/extract-contacts`,
      { text },
      { headers: getHeaders(token) }
    );
    return response.data;
  } catch (err: unknown) {
    captureException(err);
    const e = err as { response?: { data?: { error?: string } } };
    return { success: false, error: e?.response?.data?.error || "Failed to extract contacts" };
  }
};


// ============================================
// AI MARKETING — single-member email draft
// ============================================
export type MemberEmailIntent = "payment_reminder" | "welcome_back" | "schedule_change" | "follow_up" | "congrats" | "reactivation" | "custom";

export interface AIMemberEmailDraft {
  subject: string;
  body: string;
  rationale: string;
  member_context_used: {
    name: string;
    balance: number | null;
    athletes_count: number;
    active_enrollments_count: number;
    outstanding_invoices_count: number;
  };
  usage?: { input_tokens: number; output_tokens: number };
  _parse_failed?: boolean;
}

export const draftMemberEmail = async (
  token: string,
  payload: {
    membership_id: string;
    intent?: MemberEmailIntent;
    goal?: string;
    tone?: AIDraftTone;
  }
): Promise<{ success: boolean; data?: AIMemberEmailDraft; error?: string } | null> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/ai/marketing/draft-member-email`,
      payload,
      { headers: getHeaders(token) }
    );
    return response.data;
  } catch (err: unknown) {
    const e = err as { response?: { status?: number; data?: { error?: string } } };
    const status = e?.response?.status;
    // 404 = the member was deleted/archived between page load and this
    // click. Not a code bug — surface a friendly message and skip Sentry
    // so we don't pollute the issue stream (CLUB-CENTRAL-TENANT-3K).
    if (status === 404) {
      return {
        success: false,
        error: "This member is no longer available. Refresh the page and try again.",
      };
    }
    captureException(err);
    return { success: false, error: e?.response?.data?.error || "Failed to draft email" };
  }
};


// ============================================
// AI INSIGHTS — owner summary, configurable comparison period
// ============================================
export type InsightsPeriod = "7d" | "14d" | "30d" | "90d" | "mtd" | "ytd" | "yoy";

export const INSIGHTS_PERIOD_OPTIONS: { value: InsightsPeriod; label: string }[] = [
  { value: "7d", label: "Last 7 days" },
  { value: "14d", label: "Last 14 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "mtd", label: "Month-to-date" },
  { value: "ytd", label: "Year-to-date" },
  { value: "yoy", label: "Year over year" },
];

export interface InsightsMetric {
  current: number;
  prior: number;
  pct_change: number;
}

export interface InsightsPeriodInfo {
  key: InsightsPeriod;
  label: string;
  current: { start: string; end: string };
  prior: { start: string; end: string };
}

export interface InsightsSnapshot {
  period: InsightsPeriodInfo;
  revenue: InsightsMetric;
  payments: InsightsMetric;
  new_memberships: InsightsMetric;
  new_enrollments: InsightsMetric;
  cancelled_enrollments: InsightsMetric;
  attendance: InsightsMetric;
  new_leads: InsightsMetric;
  converted_leads: InsightsMetric;
}

export interface InsightsSummary {
  headline: string;
  bullets: string[];
  watch_this: string;
  verdict: "strong" | "mixed" | "weak";
  raw_snapshot: InsightsSnapshot;
  period: InsightsPeriodInfo;
  generated_at: string;
  model: string;
  _parse_failed?: boolean;
}

export const getInsightsSummary = async (
  token: string,
  period: InsightsPeriod = "30d"
): Promise<{ success: boolean; data?: InsightsSummary; error?: string } | null> => {
  try {
    const response = await axios.get(`${BASE_URL}/ai/insights/weekly-summary`, {
      headers: getHeaders(token),
      params: { period },
    });
    return response.data;
  } catch (err: unknown) {
    captureException(err);
    const e = err as { response?: { data?: { error?: string } } };
    return { success: false, error: e?.response?.data?.error || "Failed to load AI summary" };
  }
};


// ============================================
// AI STAFF CHAT — Quick Answers panel
// ============================================
export interface StaffChatResponse {
  response: string;
  session_id: string;
  conversation_id: string | null;
}

export const sendStaffAiMessage = async (
  message: string,
  sessionId?: string
): Promise<{ success: boolean; data?: StaffChatResponse; error?: string }> => {
  try {
    const token = localStorage.getItem("authToken");
    const response = await axios.post(
      `${BASE_URL}/ai/staff-chat`,
      { message, session_id: sessionId },
      { headers: getHeaders(token || undefined) }
    );
    return response.data;
  } catch (err: unknown) {
    captureException(err);
    const e = err as { response?: { data?: { message?: string; error?: string } } };
    return {
      success: false,
      error: e?.response?.data?.message || e?.response?.data?.error || "Failed to send message",
    };
  }
};

// ============================================
// SOCIAL MARKETING AI
// ============================================

export type SocialPlatform = "facebook" | "instagram" | "x" | "linkedin";

export interface SocialPostVariant {
  caption: string;
  hashtags: string[];
  alt_text: string;
  rationale: string;
}

export interface SocialPostResponse {
  success: boolean;
  data?: { variants: SocialPostVariant[] };
  error?: string;
}

export const draftSocialPost = async (
  token: string,
  body: {
    goal: string;
    platform: SocialPlatform;
    tone?: string;
    brandVoice?: string;
    variantCount?: number;
  }
): Promise<SocialPostResponse> => {
  try {
    const res = await axios.post(`${BASE_URL}/ai/marketing/draft-social-post`, body, {
      headers: getHeaders(token),
    });
    return res.data;
  } catch (err: unknown) {
    const e = err as { response?: { data?: { error?: string; message?: string } } };
    return {
      success: false,
      error: e?.response?.data?.error || e?.response?.data?.message || "Failed to draft post",
    };
  }
};

export interface CalendarPost {
  day_offset: number;
  platform: SocialPlatform;
  topic: string;
  caption: string;
  hashtags: string[];
  alt_text: string;
}

export interface CalendarResponse {
  success: boolean;
  data?: { posts: CalendarPost[] };
  error?: string;
}

export const generateSocialCalendar = async (
  token: string,
  body: {
    count?: number;
    focus?: "balanced" | "program_promos" | "community" | "events" | "training_tips";
    tone?: string;
    brandVoice?: string;
  }
): Promise<CalendarResponse> => {
  try {
    const res = await axios.post(`${BASE_URL}/ai/marketing/social-content-calendar`, body, {
      headers: getHeaders(token),
    });
    return res.data;
  } catch (err: unknown) {
    const e = err as { response?: { data?: { error?: string; message?: string } } };
    return {
      success: false,
      error: e?.response?.data?.error || e?.response?.data?.message || "Failed to generate calendar",
    };
  }
};

// ============================================
// REPUTATION AI
// ============================================

export interface FeedbackTheme {
  name: string;
  mentions: number;
  sentiment: "positive" | "negative" | "mixed";
  sample_quote: string;
}

export interface FeedbackAnalysis {
  headline_findings: string[];
  themes: FeedbackTheme[];
  sentiment: { positive_pct: number; neutral_pct: number; negative_pct: number };
  suggested_actions: string[];
  analyzed_count: number;
  empty: boolean;
}

export const analyzeFeedback = async (
  token: string,
  body: { from_date?: string; to_date?: string; limit?: number }
): Promise<{ success: boolean; data?: FeedbackAnalysis; error?: string }> => {
  try {
    const res = await axios.post(`${BASE_URL}/ai/reputation/analyze-feedback`, body, {
      headers: getHeaders(token),
    });
    return res.data;
  } catch (err: unknown) {
    const e = err as { response?: { data?: { error?: string; message?: string } } };
    return {
      success: false,
      error: e?.response?.data?.error || e?.response?.data?.message || "Failed to analyze feedback",
    };
  }
};

export type ReviewPlatform = "google" | "yelp" | "facebook" | "other";
export type ReviewTone = "warm" | "professional" | "apologetic" | "enthusiastic";

export interface ReviewResponseDraft {
  response: string;
  rationale: string;
  length_chars: number;
}

export const draftReviewResponse = async (
  token: string,
  body: {
    review_text: string;
    rating?: number;
    reviewer_name?: string;
    platform?: ReviewPlatform;
    tone?: ReviewTone;
    brandVoice?: string;
  }
): Promise<{ success: boolean; data?: ReviewResponseDraft; error?: string }> => {
  try {
    const res = await axios.post(`${BASE_URL}/ai/reputation/draft-review-response`, body, {
      headers: getHeaders(token),
    });
    return res.data;
  } catch (err: unknown) {
    const e = err as { response?: { data?: { error?: string; message?: string } } };
    return {
      success: false,
      error: e?.response?.data?.error || e?.response?.data?.message || "Failed to draft response",
    };
  }
};

